/**
 * Product card content that depends on the customer's selections: the
 * representative variant behind a card's price and image, the image fallback
 * chain, and the derived feature rows. Pure functions over the cached product
 * payload, implementing the Product Card Drawers spec's "Representative
 * Variant", "Card Image" and "Derived Rows" sections.
 */
import { equal, lessThan } from "dinero.js";
import { Immutable } from "immer";
import { msgid, ngettext } from "ttag";

import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValues,
    IProduct,
} from "../../models/catalogue";
import {
    PriceType,
    getOptionValueDisplayName,
    getProductPrice,
} from "../../utils/catalogue";
import {
    IDisplayOrder,
    LEVEL_CODE,
    asTuple,
    availableTuples,
    candidateVariants,
    sameValue,
    valueOf,
    variantsOf,
} from "./availability";
import { ICardContentRootProduct, ICardContentValue } from "./models";

type IImage = Immutable<IProduct["images"][number]>;

const unitPrice = (variant: Immutable<IProduct>) =>
    getProductPrice(variant.price, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });

/**
 * The available variants a card for `value` can stand for, best first: they
 * match the selections in the drawers before this one (never later ones, so
 * a card's price doesn't move as later drawers change) and carry `value`.
 * Ordered by price, then by the render order of each later drawer, then by
 * product ID, so the result is a pure function of the root product, earlier
 * selections and the option value.
 */
export const rankedCardVariants = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
    code: IOptionCode,
    value: IOptionSingleValue,
    displayOrder: IDisplayOrder,
): Immutable<IProduct>[] => {
    const position = orderedCodes.indexOf(code);
    const laterCodes = orderedCodes.slice(position + 1);
    // The same candidates the drawer's value list comes from.
    const candidates = candidateVariants(
        root,
        orderedCodes,
        selections,
        code,
    ).filter((variant) => sameValue(valueOf(variant, code), value));
    const laterOrders = new Map(
        laterCodes.map((later) => [
            later,
            displayOrder(later, [
                ...new Set(
                    candidates
                        .map((variant) => asTuple(valueOf(variant, later))?.[0])
                        .filter((v): v is IOptionSingleValue => !!v),
                ),
            ]),
        ]),
    );
    const rank = (variant: Immutable<IProduct>, later: IOptionCode): number => {
        const first = asTuple(valueOf(variant, later))?.[0];
        const idx =
            first === undefined
                ? -1
                : (laterOrders.get(later) ?? []).indexOf(first);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...candidates].sort((a, b) => {
        const priceA = unitPrice(a);
        const priceB = unitPrice(b);
        if (!equal(priceA, priceB)) {
            return lessThan(priceA, priceB) ? -1 : 1;
        }
        for (const later of laterCodes) {
            const diff = rank(a, later) - rank(b, later);
            if (diff !== 0) {
                return diff;
            }
        }
        return Number(a.id) - Number(b.id);
    });
};

const imageWithRole = (
    product: Immutable<IProduct>,
    role: string,
): IImage | null => product.images.find((img) => img.role === role) ?? null;

/**
 * Card image fallback chain: the representative variant's PLP Config image,
 * then any other candidate variant's (cheapest first), then the root
 * product's PLP Config, Product Tile, and first image.
 */
export const cardImage = (
    root: Immutable<IProduct>,
    rankedVariants: readonly Immutable<IProduct>[],
): IImage | null => {
    for (const variant of rankedVariants) {
        const image = imageWithRole(variant, "PLP Config");
        if (image) {
            return image;
        }
    }
    return (
        imageWithRole(root, "PLP Config") ??
        imageWithRole(root, "Product Tile") ??
        root.images.at(0) ??
        null
    );
};

export type IDerivedRow = Readonly<{
    code: IOptionCode;
    label: string;
    values: readonly IOptionSingleValue[];
}>;

/**
 * Default label for the derived row of an option code. Singular and plural
 * are separate catalogue strings per option code; a new option code needs
 * its pair added here.
 */
const defaultDerivedRowLabel = (code: IOptionCode, count: number): string => {
    switch (code) {
        case "option_feel":
            return ngettext(msgid`Available Feel`, `Available Feels`, count);
        case "option_size":
            return ngettext(msgid`Available Size`, `Available Sizes`, count);
        case "option_model":
            return ngettext(msgid`Available Model`, `Available Models`, count);
        case "option_color":
            return ngettext(msgid`Available Color`, `Available Colors`, count);
        case "option_height":
            return ngettext(
                msgid`Available Height`,
                `Available Heights`,
                count,
            );
        case "option_profile":
            return ngettext(
                msgid`Available Profile`,
                `Available Profiles`,
                count,
            );
        default:
            return ngettext(
                msgid`Available Option`,
                `Available Options`,
                count,
            );
    }
};

/**
 * Rows describing the drawer immediately after the card's drawer: the values
 * available with this card's value, grouped by the next option's labeled
 * groups. A card on the last drawer, or before the Level Selection Panel,
 * has none. Multi-value options contribute every value in any available
 * tuple, in the first selector's order.
 */
export const derivedRows = (
    root: Immutable<IProduct>,
    orderedCodes: readonly IOptionCode[],
    selections: Immutable<IOptionValues>,
    code: IOptionCode,
    value: IOptionSingleValue,
    displayOrder: IDisplayOrder,
): IDerivedRow[] => {
    const position = orderedCodes.indexOf(code);
    const next = position === -1 ? undefined : orderedCodes.at(position + 1);
    if (next === undefined || next === LEVEL_CODE) {
        return [];
    }
    const values = displayOrder(next, [
        ...new Set(
            availableTuples(
                root,
                orderedCodes,
                { ...selections, [code]: value },
                next,
                displayOrder,
            ).flat(),
        ),
    ]);
    if (values.length === 0) {
        return [];
    }
    const groups = root.attributes.product_option_labels?.value[next] ?? [];
    const rows: IDerivedRow[] = [];
    for (const group of groups) {
        const groupValues = values.filter((v) => group.values.includes(v));
        if (groupValues.length > 0) {
            rows.push({
                code: next,
                label:
                    groupValues.length === 1 && group.label_singular
                        ? group.label_singular
                        : group.label,
                values: groupValues,
            });
        }
    }
    const uncovered = values.filter(
        (v) => !groups.some((group) => group.values.includes(v)),
    );
    if (uncovered.length > 0) {
        rows.push({
            code: next,
            label: defaultDerivedRowLabel(next, uncovered.length),
            values: uncovered,
        });
    }
    return rows;
};

/**
 * A value's display name (translated where the attribute carries display
 * names), read from any variant carrying it.
 */
export const optionDisplayName = (
    root: Immutable<IProduct>,
    code: IOptionCode,
    value: IOptionSingleValue,
): string => {
    for (const variant of variantsOf(root)) {
        const attr = variant.attributes[code];
        if (attr && sameValue(valueOf(variant, code), value)) {
            return getOptionValueDisplayName({ ...attr, value });
        }
    }
    return value;
};

/**
 * The card content record for one option value, or null when none was
 * authored.
 */
export const cardValueContent = (
    rootContent: ICardContentRootProduct | null,
    code: IOptionCode,
    value: IOptionSingleValue,
): ICardContentValue | null => {
    if (!rootContent || !Object.hasOwn(rootContent.options, code)) {
        return null;
    }
    const option = rootContent.options[code];
    return Object.hasOwn(option.values, value) ? option.values[value] : null;
};
